import { ACCESS_TYPES, COMPANIES_LIST_TAB, CONSTANTS, DEAL_VIEW_TYPES } from './constants';
import type { ShowUnlockAicaAndAddToLeadsPayload } from './action';
import { MasterData } from '../../common/DealApplicationDrawer/customHooks/useDADataTransformations';
import { USER_TYPE } from '../../utils/constants/user';
import { StatusDropdownOption } from '../../types/statusTimeline';
import { formatFollowUpDateTime } from '../../utils/commonUtil';

export interface PortfolioCompanyRow {
    // isUnlocked: boolean;
    profileGenerationStatus: string;
    action: string;
    aicaScore: string;
    assesseeOrgId: string;
    contactEmail: string;
    contactPerson: string;
    gstConnected: string;
    lastFetched: string;
    lastUpdated: string;
    orgName: string;
    reportId: string;
    companyAccessType: string;
    demoOrg?: boolean;
    unlockAt?: string;
    journeyName?: string;
    followUpDetails?: {
        text: string;
        subText?: string;
    };
}

interface Owner {
    id: string;
    name: string;
}

interface Owners {
    dealOwners?: Owner[];
    uwAnalysts?: Owner[];
    dcmOwners?: Owner[];
    leadOwners?: Owner[];
}

export type AddToWatchlistModalPayload = {
    show: boolean;
    data?: {
        prefillIdentifier?: string;
        prefillIdentifierDisplay?: string;
        prefillIdentifierPayload?: string;
        searchText?: string;
        result?: any;
        companyInfo?: any;
        companyInfoLoading?: boolean;
        prefillLoadingFields?: { identifier?: boolean; website?: boolean; brand?: boolean };
        overwriteIdentifierFromCompanyInfo?: boolean;
        isAddUsingSearchText?: boolean;
        shouldOpenDropdown?: boolean;
        source?: string;
    };
    onSuccess?: (() => void) | null;
};

export interface DealFilters {
    dealReceivers?: string[];
    statuses?: string[];
    dealOwners?: string[];
    uwAnalysts?: string[];
    dcmOwners?: string[];
    leadOwners?: string[];
    products?: string[];
}

export interface AicaDebtProductConfig {
    columns?: {
        id: string;
        label: string;
    }[];
    filters?: {
        showFilters: boolean;
        filterType: string;
        pages: {
            step: string;
            text: string;
            ctas: {
                primary: {
                    text: string;
                    submitSource: string;
                };
                secondary: {
                    text: string;
                    submitSource: string;
                };
            };
            config: {
                text: string;
                sectionKey: string;
                config: Record<
                    string,
                    {
                        isRequired: boolean;
                        type: string;
                        placeholder?: string;
                        label: string;
                        unit?: string;
                        minimumValue?: number;
                        maximumValue?: number;
                        size?: {
                            column: string;
                        };
                        api?: string;
                    }
                >;
            }[];
        }[];
    };
    valueMapping?: {
        probability: { key: string };
        roi: { key: string };
        tenure: { key: string };
        security: { key: string };
        lenderPf: { key: string };
        timeline: { key: string };
        amount: { key: string };
        terms: Record<string, { key: string; text: string }>;
        eligibility: Record<string, { key: string; text: string; tooltip?: string }>;
    };
    ratingValueMapping?: Record<string, number>;
    visibilityStates?: {
        accordion: boolean;
        selectProduct: boolean;
        selectLender: boolean;
        createDeal: boolean;
        editProfile: boolean;
    };
    debtProductOverviewTableHeaders?: {
        tentativeTerms: string[];
        eligibilityCriteria: string[];
    };
}

export interface ColumnCustomizationState {
    open: boolean;
    sourceKey: string;
    selectedColumns: string[];
    tempSelectedColumns: string[];
    isModified: boolean;
}

export interface CustomFilterDrawerState {
    open: boolean;
    appliedFilters?: {
        [sourceKey: string]: {
            [field: string]: any[];
        };
    };
    columnCustomization?: ColumnCustomizationState;
}

interface InvestorReducer {
    orgInfo: {};
    fetchingConfig: boolean;
    fetchingPortfolio: boolean;
    fetchingDeals: boolean;
    showUpgradeToScan: string;
    showRefreshReport: boolean;
    untaggedCount: number;
    pendingDataCount: number | null;
    visibilityConfig: any | null; // Cached AICA_VISIBILITY config
    unlockAicaReport: {
        showUnlockAicaConfirmation: boolean;
        orgId: string;
        orgName: string;
    };
    unlockAicaReportAndAddToLeads: {
        showConfirmationModal: boolean;
        assesseeOrgId: string;
        orgName: string;
        source: string;
    };
    showInviteUser: boolean;
    showGlobalSearch: boolean;
    composeEmailData: {
        showComposeEmailModal: boolean;
        selectedRequirements: any[];
        orgName: string;
        source: string;
        orgId?: string;
        lenderId?: string;
        dealId?: string;
        partnerOrgId?: string;
    };
    createMandateModal: {
        show: boolean;
    };
    configTableModal: {
        show: boolean;
        data: {
            entity?: 'Deal' | 'Lead' | 'Mandate';
            actionType?: string;
            dropdownOption?: string;
            triggers?: Array<{ actionType: string; values: string[] }>;
            rowData?: any;
            source?: ('Deal' | 'Lead' | 'Mandate') | ('Deal' | 'Lead' | 'Mandate')[];
            dealId?: string;
            orgId?: string; // assesseeOrgId/dealBeneficiaryId for Lead data
            mandateId?: string; // mandateId if applicable
            onSuccess?: (data?: any) => void; // Called after successful API operations
            onCloseCallback?: () => void; // Called when modal is closed (cancel, X button, etc.)
        };
    };
    addToWatchlistModal: AddToWatchlistModalPayload;

    inviteUserCallback?: (() => void) | undefined;
    searchingPortfolio: boolean;
    config: null | InvestorConfig;
    portfolio: Portfolio;
    role: null | string;
    selectedCompany: null | { [key: string]: unknown };
    permissions: {
        fetching: boolean;
        permissionsFetched: boolean;
        SCAN: string;
        WATCH: string;
        TRACK: string;
        DEALS: string;
        onboardingTabs: string[];
        showMagicUpload?: boolean;
        showDataGap?: boolean;
    };
    ownerDropdownOptions: {
        userId: string;
        emailId: string;
        userType: string;
        name?: string;
        mobileNumber?: string;
        customSortName?: string;
        countryCode?: string;
    }[];
    partnerData: {
        userId: string;
        organizationName: string;
        emailId: string;
        userType: string;
        organizationId: string;
        userStatus?: string;
        phoneVerified?: boolean;
        emailVerified?: boolean;
        countryCode?: string;
        mobileNumber?: string;
        organizationDisplayName?: string;
    }[];
    dealApplicationDrawer: DealApplicationDrawerState;
    dealDrawer: DealDrawerState;
    dealTermsLoggingDrawer: DealTermsLoggingDrawerState;
    deals: Deals;

    tabLockStatus: null | { isLocked: boolean };
    modulePollingList: string[];
    taskTrackerMap: {
        [taskTrackerStep: string]: { status: string; dateCreated: number };
    };
    onePager: OnePagerData;
    engagementLetter: EngagementLetterData;
    bureauAndKYCConsentLetter: BureauAndKYCConsentLetterData;
    nda: NDAData;
    applicationForm: ApplicationFormData;
    owners: Owners;
    dealFilters: DealFilters;
    showManageTeamDrawer: boolean;
    aicaDebtProductConfig: AicaDebtProductConfig;
    customFilterDrawer: CustomFilterDrawerState;
    shouldRefreshRequirements: boolean;
    editUserModal: {
        show: boolean;
        userData: any;
        refetchUsers: boolean;
    };
    platformUserData?: any;
    deleteUserModal: {
        show: boolean;
        userData: any;
    };
    extendedPollingModules: string[];
}

interface InvestorConfig {
    tableHeaders: object;
    dataConfig?: any;
    requiredMonthsForGstData?: number;
    additionalDetailsConfig?: any;
    tabConfigs?: any;
}
interface Portfolio {
    portfolioFetched: boolean;
    currentPageNumber: number;
    hasNextPage: boolean;
    totalPortfolioCount: number;
    newCompaniesCount: number;
    viewedCompaniesCount: number;
    monitoringCompaniesCount: number;
    nonMonitoringCompaniesCount: number;
    fetchedType: string;
    companiesList: PortfolioCompanyRow[];

    companiesListFilters: {
        filterConfig: {
            configReady: boolean;
            components: { filterId: string; filterName: string; options: { label: string; value: string }[] }[];
        };
        filterValues: {
            [key: string]: string[];
        };
    };
}

export type DealDrawerState = {
    open: boolean;
    drawerLabel?: string;
    companyId?: string;
    companyName?: string;
    dealId?: string;
    lenderDealId?: string;
    readonly?: boolean;
    step?: string;
    profileGenerationStatus?: string;
    isPec?: boolean;
    drawerSource?: string;
    selectedStep?: number;
    isLenderOrg?: boolean;
    userType?: string;
};

export type DealTermsLoggingDrawerState = {
    prefilledStatus: string | null;
};

export type DealApplicationDrawerState = {
    open: boolean;
    width: string;
    source: string;
    showSuccessModal?: boolean;
    heading: string;
    subHeading: string;
    dealBeneficiaryId?: string;
    dealMasterData?: MasterData;
    profileGenerationStatus?: string;
    dealId?: string;
    allProducts?: {
        label: string;
        value: string;
    }[];
    step?: number;
    disabledFields?: string[];
};

export interface DealData {
    dealId: string;
    dealProposerId: string;
    dealBeneficiaryId: string;
    borrowerName: string;
    lenderName?: string;
    amount: string;
    tenure: string;
    roi: string;
    loanTypes: string[];
    createdAt?: number;
    security: string[];
    dealStatus: string;
    dealNotes: string;
    comment?: {
        text: string;
        activityBy: string;
        activityDate: string;
        userId: string;
    };
    sharedDate: string;
    borrowerDetails: {
        companyAge: string;
        companySector: string;
        annualRevenue: string;
        growthRate: string;
        runway: string;
        networth: string;
        ebitda: string;
        equityRaised: string;
    };
    docIds: [];
    documents?: { action: string; docId: string; documentName: string }[];
    owners: {
        [key: string]: string;
    };
    mainConfig?: {
        actionConfig: string[];
    };
    followUpDetails?: {
        text: string;
        subText?: string;
    };
    customFields?: { [key: string]: any };
}

export interface InvestorDealData extends DealData {
    dealReceiverId: string;
    lenderName: string;
    id?: string;
    followUpDetails?: {
        text: string;
        subText?: string;
    };
}

export type FetchDealsData = DealData & {
    id?: string;
    investorDeals: InvestorDealData[];
};

export type Deals = {
    pageNumber: number;
    hasNextPage: boolean;
    data: FetchDealsData[];
    dealCount: {
        totalDeals: number;
        approvedDeals: number;
        sharedDeals: number;
        passedDeals: number;
        filteredDeals: number;
    };
    dealStatusConfig: null | DealStatusConfig;
};

export const MANDATE = 'mandate';
export const DEAL = 'deal';

export type DealStatusConfig = {
    [DEAL]: StatusDropdownOption[];
    [MANDATE]: StatusDropdownOption[];
    statusDropdownOptionsMap: Record<string, StatusDropdownOption>;
    isDisableRowLogicEnabled?: boolean;
};

export type OnePagerData = {
    open: boolean;
    source: null | 'PORTFOLIO' | 'REPORT_TOP_BAR' | 'CREATE_DEAL';
    loading?: boolean;
    assesseeOrgId: null | string;
    companyName?: string;
    dealFormData?: null | Object;
    categoryId?: number;
    flowId?: number;
};

export type EngagementLetterData = {
    open: boolean;
    source: null | 'PORTFOLIO' | 'REPORT_TOP_BAR';
    loading?: boolean;
    orgId: null | string;
    companyName?: string;
};

export type BureauAndKYCConsentLetterData = {
    open: boolean;
    source: null | 'PORTFOLIO' | 'REPORT_TOP_BAR';
    loading?: boolean;
    orgId: null | string;
    companyName?: string;
};

export type NDAData = {
    open: boolean;
    source: null | 'PORTFOLIO' | 'REPORT_TOP_BAR';
    loading?: boolean;
    orgId: null | string;
    companyName?: string;
};

export type ApplicationFormData = {
    open: boolean;
    source: null | 'PORTFOLIO' | 'REPORT_TOP_BAR';
    loading?: boolean;
    orgId: null | string;
    companyName?: string;
};

export enum DealStatus {
    SHARED = 'SHARED',
    IPA_RECEIVED = 'IPA_RECEIVED',
    PASSED_PRE_IPA = 'PASSED_PRE_IPA',
    NEED_INFO_PRE_IPA = 'NEED_INFO_PRE_IPA',
    IPA_AWAITED = 'IPA_AWAITED',
    IPA_ACCEPTED = 'IPA_ACCEPTED',
    NEED_INFO_POST_IPA = 'NEED_INFO_POST_IPA',
    SL_AWAITED = 'SL_AWAITED',
    SL_RECEIVED = 'SL_RECEIVED',
    SL_ACCEPTED = 'SL_ACCEPTED',
    NEED_INFO_POST_SL = 'NEED_INFO_POST_SL',
    DISBURSAL_PENDING = 'DISBURSAL_PENDING',
    DISBURSED = 'DISBURSED',
    FEE_RECEIVED = 'FEE_RECEIVED',
    UNRESPONSIVE_PRE_IPA = 'UNRESPONSIVE_PRE_IPA',
    UNRESPONSIVE_POST_IPA = 'UNRESPONSIVE_POST_IPA',
    PASSED_POST_IPA = 'PASSED_POST_IPA',
    DRAFT = 'DRAFT',
    REVIEW_BASE_DATA = 'REVIEW_BASE_DATA',
    FIRST_DEAL_SHARED = 'FIRST_DEAL_SHARED',
    FIRST_INTEREST_RECEIVED = 'FIRST_INTEREST_RECEIVED',
    FIRST_IPA_RECEIVED = 'FIRST_IPA_RECEIVED',
    FIRST_IPA_ACCEPTED = 'FIRST_IPA_ACCEPTED',
    FIRST_SL_RECEIVED = 'FIRST_SL_RECEIVED',
    FIRST_SL_ACCEPTED = 'FIRST_SL_ACCEPTED',
    FIRST_DEAL_DISBURSED = 'FIRST_DEAL_DISBURSED',
    ALL_UNRESPONSIVE = 'ALL_UNRESPONSIVE',
    ALL_PASSED = 'ALL_PASSED',
    INTEREST_RECEIVED = 'INTEREST_RECEIVED',
    INTERESTED = 'INTERESTED',
    UNRESPONSIVE = 'UNRESPONSIVE',
    PASSED = 'PASSED',
}

export const defaultDealFilters: DealFilters = {
    dealReceivers: [],
    statuses: [],
    dealOwners: [],
    uwAnalysts: [],
    dcmOwners: [],
    leadOwners: [],
    products: [],
};
const initialState: InvestorReducer = {
    fetchingConfig: false,
    fetchingPortfolio: false,
    fetchingDeals: false,
    searchingPortfolio: false,
    role: '',
    untaggedCount: 0,
    pendingDataCount: null,
    config: null,
    visibilityConfig: null,
    showUpgradeToScan: '',
    showRefreshReport: false,
    unlockAicaReport: {
        showUnlockAicaConfirmation: false,
        orgId: '',
        orgName: '',
    },
    unlockAicaReportAndAddToLeads: {
        showConfirmationModal: false,
        assesseeOrgId: '',
        orgName: '',
        source: '',
    },
    showInviteUser: false,
    showGlobalSearch: false,
    composeEmailData: {
        showComposeEmailModal: false,
        selectedRequirements: [],
        orgName: '',
        source: '',
        orgId: '',
        lenderId: '',
        dealId: '',
    },
    createMandateModal: {
        show: false,
    },
    configTableModal: {
        show: false,
        data: {},
    },
    addToWatchlistModal: {
        show: false,
        data: {},
        onSuccess: null,
    },
    inviteUserCallback: undefined,
    selectedCompany: { companyAccessType: '' },
    portfolio: {
        portfolioFetched: false,
        currentPageNumber: 1,
        hasNextPage: true,
        totalPortfolioCount: 0,
        newCompaniesCount: 0,
        viewedCompaniesCount: 0,
        monitoringCompaniesCount: 0,
        nonMonitoringCompaniesCount: 0,
        fetchedType: COMPANIES_LIST_TAB.ALL,
        companiesList: [],

        companiesListFilters: {
            filterConfig: {
                configReady: false,
                components: [],
            },
            filterValues: {},
        },
    },
    ownerDropdownOptions: [],
    partnerData: [],
    permissions: {
        fetching: true,
        permissionsFetched: false,
        SCAN: ACCESS_TYPES.LOCKED,
        WATCH: ACCESS_TYPES.LOCKED,
        TRACK: ACCESS_TYPES.LOCKED,
        DEALS: ACCESS_TYPES.LOCKED,
        onboardingTabs: [],
        showMagicUpload: false,
    },
    dealApplicationDrawer: {
        open: false,
        heading: '',
        subHeading: '',
        source: '',
        width: '0%',
        dealId: '',
        step: 0,
    },
    dealDrawer: {
        open: false,
        drawerSource: '',
        drawerLabel: '',
        selectedStep: 1,
    },
    dealTermsLoggingDrawer: {
        prefilledStatus: null,
    },
    deals: {
        pageNumber: 1,
        hasNextPage: false,
        data: [],
        dealCount: {
            totalDeals: 0,
            approvedDeals: 0,
            sharedDeals: 0,
            passedDeals: 0,
            filteredDeals: 0,
        },
        dealStatusConfig: null,
    },
    tabLockStatus: null,
    modulePollingList: [],
    taskTrackerMap: {},
    onePager: {
        source: null,

        open: false,
        loading: false,
        assesseeOrgId: null,
        dealFormData: null,
    },
    engagementLetter: {
        open: false,
        source: null,
        loading: false,
        orgId: null,
    },
    bureauAndKYCConsentLetter: {
        open: false,
        source: null,
        loading: false,
        orgId: null,
    },
    nda: {
        open: false,
        source: null,
        loading: false,
        orgId: null,
    },
    applicationForm: {
        open: false,
        source: null,
        loading: false,
        orgId: null,
    },
    owners: {
        dealOwners: [],
        uwAnalysts: [],
        dcmOwners: [],
        leadOwners: [],
    },
    dealFilters: defaultDealFilters,
    showManageTeamDrawer: false,
    aicaDebtProductConfig: {},
    customFilterDrawer: {
        open: false,
        appliedFilters: {},
    },
    shouldRefreshRequirements: false,
    editUserModal: {
        show: false,
        userData: null,
        refetchUsers: false,
    },
    orgInfo: {
        owner: null,
    },
    platformUserData: undefined,
    deleteUserModal: {
        show: false,
        userData: null,
    },
    extendedPollingModules: [],
};

const createOwnerUpdate = (action: any, company: any) => {
    if (!action?.payload?.owner?.ownerType) {
        return {};
    }

    const { ownerType, userId } = action.payload.owner;

    return {
        owners: {
            ...company.owners,
            [ownerType]: userId,
        },
    };
};

const ACTIVE_STATUSES = [
    'ATTEMPT_BEING_MADE',
    'NEW',
    'ASSIGNED',
    'MEETING_BOOKED',
    'INTERESTED',
    'MANDATE_CREATED',
    'CONVERSTION_ONGOING',
    'DEAL_ONGOING',
];

const ACTIVE_DEAL_STATUSES = [
    'SHARED',
    'SHARED_DEAL',
    'INTERESTED',
    'INTEREST_RECEIVED',
    'IPA_ACCEPTED',
    'IPA_RECEIVED',
    'SL_RECEIVED',
    'SL_ACCEPTED',
];
//--------LOADERS----------//

const setIsFetchingConfig = (state, action) => {
    return {
        ...state,
        fetchingConfig: action?.payload,
    };
};

const setIsFetchingPortfolio = (state, action) => {
    return {
        ...state,
        fetchingPortfolio: action?.isSearching ? false : action?.payload,
        searchingPortfolio: action?.isSearching,
    };
};

const setShowUpgradeToScan = (state, action) => {
    return {
        ...state,
        showUpgradeToScan: action?.payload,
    };
};

const setShowRefreshReport = (state, action) => {
    return {
        ...state,
        showRefreshReport: action?.payload,
    };
};

const showUnlockAicaConfirmation = (state, action) => {
    return {
        ...state,
        unlockAicaReport: action?.payload,
    };
};

const setIsFetchingDeals = (state, action) => {
    return {
        ...state,
        fetchingDeals: action.payload,
    };
};

const setIsFetchingPermissions = (state, action) => {
    return {
        ...state,
        permissions: {
            ...state.permissions,
            fetching: action.payload.fetching,
            permissionsFetched: action.payload.permissionsFetched,
        },
    };
};

const setOnePagerData = (state, action) => {
    return {
        ...state,
        onePager: {
            ...state.onePager,
            ...action.payload,
        },
    };
};

const closeOnePager = (state, action) => {
    return {
        ...state,
        onePager: {
            source: null,

            open: false,
            loading: false,
            assesseeOrgId: null,
            dealFormData: null,
        },
    };
};

const setEngagementLetterData = (state, action) => {
    return {
        ...state,
        engagementLetter: {
            ...state.engagementLetter,
            ...action.payload,
        },
    };
};

const setBureauKycConsentData = (state, action) => {
    return {
        ...state,
        bureauAndKYCConsentLetter: {
            ...state.bureauAndKYCConsentLetter,
            ...action.payload,
        },
    };
};

const closeEngagementLetter = (state, action) => {
    return {
        ...state,
        engagementLetter: {
            open: false,
            source: null,
            loading: false,
            orgId: null,
        },
    };
};

const closeBureauKycConsent = (state, action) => {
    return {
        ...state,
        bureauAndKYCConsentLetter: {
            open: false,
            source: null,
            loading: false,
            orgId: null,
        },
    };
};

const setNDAData = (state, action) => {
    return {
        ...state,
        nda: {
            ...state.nda,
            ...action.payload,
        },
    };
};

const closeNDA = (state, action) => {
    return {
        ...state,
        nda: {
            open: false,
            source: null,
            loading: false,
            orgId: null,
        },
    };
};

const setApplicationFormData = (state, action) => {
    return {
        ...state,
        applicationForm: {
            ...state.applicationForm,
            ...action.payload,
        },
    };
};

const closeApplicationForm = (state, action) => {
    return {
        ...state,
        applicationForm: {
            open: false,
            source: null,
            loading: false,
            orgId: null,
        },
    };
};

const fetchInvestorConfigSuccess = (state, action) => {
    return {
        ...state,
        fetchingConfig: false,
        config: {
            ...state.config,
            ...action?.payload,
        },
    };
};

const fetchPortfolioDataSuccess = (state, action) => {
    return {
        ...state,
        fetchingPortfolio: false,
        searchingPortfolio: false,
        portfolio: {
            ...state.portfolio,
            portfolioFetched: true,
            ...action?.payload,
            // companiesList: [],
        },
    };
};

const initialiseConfigForCompaniesListFilters = (state, action) => {
    const { accessTier, headerConfigs, filtersList, dropdownOptions } = action.payload;
    let configComponents: { filterId: string; filterName: string }[] = [];
    let filtersObj = {};

    const selectedHeadersConfigForAccessTier = headerConfigs?.[accessTier?.toLowerCase?.()]?.headers || [];
    selectedHeadersConfigForAccessTier?.forEach((headerConfigItem) => {
        if (filtersList?.[headerConfigItem?.id]) {
            configComponents.push({
                filterId: headerConfigItem?.id,
                filterName: headerConfigItem?.label,
            });
            filtersObj[headerConfigItem?.id] = [];
        }
    });

    const existingFilters = JSON.parse(JSON.stringify(state.portfolio?.companiesListFilters?.filterValues || {}));
    const newFilters = JSON.parse(JSON.stringify(filtersObj));
    return {
        ...state,
        portfolio: {
            ...state.portfolio,
            companiesListFilters: {
                filterConfig: {
                    configReady: true,
                    components: configComponents,
                },
                ...(existingFilters !== newFilters && {
                    filterValues: filtersObj,
                }),
            },
        },
    };
};

const setCompaniesListFilterValues = (state, action) => {
    const newFilters = JSON.parse(JSON.stringify(action.payload || {}));
    const existingFilters = JSON.parse(JSON.stringify(state.portfolio?.companiesListFilters?.filterValues || {}));

    return {
        ...state,
        portfolio: {
            ...state.portfolio,
            companiesListFilters: {
                ...state.portfolio.companiesListFilters,
                ...(existingFilters !== newFilters && {
                    filterValues: {
                        ...newFilters,
                    },
                }),
            },
        },
    };
};

const resetCompaniesListFilterValues = (state, action) => {
    let filtersObj = Object.keys(state?.portfolio?.companiesListFilters?.filterValues || {}).reduce((acc, key): any => {
        acc[key] = [];
        return acc;
    }, {});
    return {
        ...state,
        portfolio: {
            ...state.portfolio,
            companiesListFilters: {
                ...state.portfolio.companiesListFilters,
                filterValues: filtersObj,
            },
        },
    };
};

const fetchOwnerDataSuccess = (state, action) => {
    return {
        ...state,
        ownerDropdownOptions: action?.payload ?? [],
    };
};

const fetchDealsDataSuccess = (state, action) => {
    return {
        ...state,
        fetchingDeals: false,
        deals: {
            ...state.deals,
            ...action.payload,
        },
    };
};

const clearDealsData = (state) => {
    return {
        ...state,
        fetchingDeals: false,
        deals: {
            pageNumber: 1,
            hasNextPage: false,
            data: [],
            dealCount: {
                totalDeals: 0,
                approvedDeals: 0,
                sharedDeals: 0,
                passedDeals: 0,
                filteredDeals: 0,
            },
            dealStatusConfig: state.deals.dealStatusConfig, // Preserve dealStatusConfig
        },
    };
};

const updateDealStatus = (state, action) => {
    if (action?.payload?.isLenderSideUsers === true) return state;
    let previousDealStatus = '';
    const dealData = state.deals.data.map((deal) => {
        if ([USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM].includes(action.payload.source) && deal.dealId === action.payload.investorDealId) {
            previousDealStatus = deal.dealStatus;
            return {
                ...deal,
                dealStatus: action.payload.dealStatus,
            };
        } else if (deal.dealId === action.payload.dealId) {
            previousDealStatus = deal.dealStatus;
            return {
                ...deal,
                dealStatus: action.payload.dealStatus,
                customFields: {
                    lastStatusUpdateTime: Date.now(),
                },
            };
        }
        return deal;
    });
    const dealCount = { ...state.deals.dealCount };
    switch (previousDealStatus) {
        case DealStatus.IPA_RECEIVED:
            dealCount.approvedDeals -= 1;
            break;
        case DealStatus.SHARED:
            dealCount.sharedDeals -= 1;
            break;
        case DealStatus.PASSED_PRE_IPA:
            dealCount.passedDeals -= 1;
            break;
        default:
            break;
    }
    switch (action.payload.dealStatus) {
        case DealStatus.IPA_RECEIVED:
            dealCount.approvedDeals += 1;
            break;
        case DealStatus.SHARED:
            dealCount.sharedDeals += 1;
            break;
        case DealStatus.PASSED_PRE_IPA:
            dealCount.passedDeals += 1;
            break;
        default:
            break;
    }
    return {
        ...state,
        deals: {
            ...state.deals,
            data: dealData,
            dealCount: dealCount,
        },
    };
};

const updateDealComments = (state, action) => {
    const commentData = action.payload.comment;

    return {
        ...state,
        deals: {
            ...state.deals,
            data: state.deals.data.map((deal) => {
                if (deal.dealId === action.payload.dealId) {
                    //Comments updation logic for edit / delete comments
                    let commentToUpdate = prepareCommentUpdate(commentData);

                    return {
                        ...deal,
                        ...(commentToUpdate && {
                            comment: commentToUpdate,
                        }),
                    };
                }
                return deal;
            }),
        },
    };
};

const fetchPartnerDataSuccess = (state, action) => {
    return {
        ...state,
        partnerData: action?.payload ?? [],
    };
};

const updateDealCustomFields = (state, action) => {
    return {
        ...state,
        deals: {
            ...state.deals,
            data: state.deals.data.map((deal) => {
                if (deal.dealId === action?.payload?.dealId) {
                    return {
                        ...deal,
                        customFields: {
                            ...deal.customFields,
                            ...action.payload.customFields,
                        },
                    };
                }
                return deal;
            }),
        },
    };
};

const updateDealLastStatusUpdateTime = (state, action) => {
    const { payload } = action;
    const { dealId, investorDealId, customFields } = payload;

    return {
        ...state,
        deals: {
            ...state.deals,
            data: state.deals.data.map((deal) => {
                // Handle regular deal update
                if (dealId && deal.dealId === dealId && !investorDealId) {
                    return {
                        ...deal,
                        customFields: {
                            ...deal.customFields,
                            ...customFields,
                        },
                    };
                }

                // Handle investor deal update in deal view
                if (investorDealId && deal.dealId === investorDealId) {
                    return {
                        ...deal,
                        customFields: {
                            ...deal.customFields,
                            ...customFields,
                        },
                    };
                }

                // Handle investor deal update in nested structure
                if (investorDealId && deal.investorDeals) {
                    const hasMatchingInvestorDeal = deal.investorDeals.some((invDeal) => invDeal.dealId === investorDealId);

                    if (hasMatchingInvestorDeal) {
                        return {
                            ...deal,
                            investorDeals: deal.investorDeals.map((investorDeal) => {
                                if (investorDeal.dealId === investorDealId) {
                                    return {
                                        ...investorDeal,
                                        customFields: {
                                            ...investorDeal.customFields,
                                            ...customFields,
                                        },
                                    };
                                }
                                return investorDeal;
                            }),
                        };
                    }
                }

                return deal;
            }),
        },
    };
};

const updateDealOwner = (state, action) => {
    return {
        ...state,
        deals: {
            ...state.deals,
            data: state.deals.data.map((deal) => {
                if (deal.dealId === action.payload.dealId) {
                    return {
                        ...deal,
                        owners: {
                            ...deal.owners,
                            [action.payload.owner?.ownerType]: action.payload.owner?.userId,
                        },
                    };
                }
                return deal;
            }),
        },
    };
};

const updateInvestorDealStatus = (state, action) => {
    const { payload } = action;

    const isDealView = payload?.selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW;
    const hasDealStatus = payload?.hasOwnProperty('dealStatus');
    const hasComment = payload?.hasOwnProperty('comment');
    const isCustomField = payload?.hasOwnProperty('customFields');
    const hasOwner = payload?.hasOwnProperty('owner');
    const hasOwners = payload?.hasOwnProperty('owners') && Array.isArray(payload.owners) && payload.owners.length > 0;

    // Convert owners array to the format { [ownerType]: userId }
    const getOwnersUpdate = (existingOwners) => {
        if (hasOwners) {
            const ownersMap = { ...existingOwners };
            payload.owners.forEach((owner) => {
                if (owner?.ownerType && owner?.userId) {
                    ownersMap[owner.ownerType] = owner.userId;
                }
            });
            return ownersMap;
        } else if (hasOwner && payload.owner?.ownerType && payload.owner?.userId) {
            return {
                ...existingOwners,
                [payload.owner.ownerType]: payload.owner.userId,
            };
        }
        return existingOwners;
    };

    const commentData = action.payload.comment;
    let commentToUpdate = prepareCommentUpdate(commentData);

    const newDealStatus = payload?.dealStatus;
    const shouldSetFollowUpDetails = newDealStatus && !ACTIVE_DEAL_STATUSES.includes(newDealStatus);
    if (isDealView && (hasDealStatus || hasComment || isCustomField || hasOwner || hasOwners)) {
        return {
            ...state,
            deals: {
                ...state.deals,
                data: state.deals.data.map((deal) => {
                    if (deal.dealId !== payload.investorDealId) return deal;

                    return {
                        ...deal,
                        ...(hasDealStatus && { dealStatus: payload.dealStatus }),
                        ...(commentToUpdate && {
                            comment: commentToUpdate,
                        }),
                        ...(isCustomField && {
                            customFields: {
                                ...deal.customFields,
                                ...payload.customFields,
                            },
                        }),
                        ...((hasOwner || hasOwners) && {
                            owners: getOwnersUpdate(deal.owners || {}),
                        }),
                        ...(shouldSetFollowUpDetails && {
                            followUpDetails: {
                                text: 'Turned Off',
                                subText: 'Last: ' + formatFollowUpDateTime(new Date()),
                            },
                        }),
                    };
                }),
            },
        };
    }

    if (hasDealStatus || hasComment || isCustomField || hasOwner || hasOwners) {
        return {
            ...state,
            deals: {
                ...state.deals,
                data: state.deals.data.map((deal) => {
                    if (deal.dealId !== payload.dealId) return deal;

                    return {
                        ...deal,
                        investorDeals: deal.investorDeals.map((investorDeal) => {
                            if (investorDeal.dealId !== payload.investorDealId) return investorDeal;

                            return {
                                ...investorDeal,
                                ...(hasDealStatus && { dealStatus: payload.dealStatus }),
                                ...(commentToUpdate && {
                                    comment: commentToUpdate,
                                }),
                                ...(isCustomField && {
                                    customFields: {
                                        ...investorDeal.customFields,
                                        ...payload.customFields,
                                    },
                                }),
                                ...((hasOwner || hasOwners) && {
                                    owners: getOwnersUpdate(investorDeal.owners || {}),
                                }),
                                ...(shouldSetFollowUpDetails && {
                                    followUpDetails: {
                                        text: 'Turned Off',
                                        subText: 'Last: ' + formatFollowUpDateTime(new Date()),
                                    },
                                }),
                            };
                        }),
                    };
                }),
            },
        };
    }

    return state;
};

const updateInvestorLenderUsers = (state, action) => {
    const { selectedCategory, allUserOptions, investorDealId, selectedLenderUsers } = action.payload;
    const isDealView = selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW;
    const updatedLenderUsers = allUserOptions
        .filter((option) => selectedLenderUsers.includes(option.value))
        .map((option) => ({
            name: option.label,
            userId: option.value,
            ownerType: option.type === 'Admin' ? 'LENDER' : 'LENDER_TEAM',
        }));

    if (isDealView) {
        return {
            ...state,
            deals: {
                ...state.deals,
                data: state.deals.data.map((deal) => {
                    if (deal.dealId !== investorDealId) return deal;

                    return {
                        ...deal,
                        lenderUsers: updatedLenderUsers,
                    };
                }),
            },
        };
    } else {
        return {
            ...state,
            deals: {
                ...state.deals,
                data: state.deals.data.map((deal) => {
                    // Only update the deal that contains the matching investorDealId
                    const hasMatchingInvestorDeal = deal?.investorDeals?.some((invDeal) => invDeal.dealId === investorDealId);

                    if (!hasMatchingInvestorDeal) return deal;

                    return {
                        ...deal,
                        investorDeals: deal.investorDeals.map((investorDeal) => {
                            if (investorDeal.dealId !== investorDealId) return investorDeal;

                            return {
                                ...investorDeal,
                                lenderUsers: updatedLenderUsers,
                            };
                        }),
                    };
                }),
            },
        };
    }
};

const updateCompanyStatus = (state, action) => {
    const companyComment = action?.payload?.companyComment;
    const additionalData = companyComment?.additionalData;

    return {
        ...state,
        portfolio: {
            ...state.portfolio,
            companiesList: state.portfolio.companiesList.map((company) => {
                if (company.assesseeOrgId === action.payload.orgId || company.assesseeOrgId === action.payload.assesseeOrgId) {
                    let commentToUpdate = prepareCommentUpdate(companyComment);

                    const newStatus = action.payload.companyStatus?.status;
                    const shouldSetFollowUpDetails = newStatus && !ACTIVE_STATUSES.includes(newStatus);

                    return {
                        ...company,
                        ...(Object.hasOwn(action?.payload || {}, 'companyStatus') && {
                            companyStatus: action.payload.companyStatus?.status,
                        }),
                        ...(Object.hasOwn(action?.payload || {}, 'companyRiskGrade') && {
                            companyRiskGrade: action.payload.companyRiskGrade,
                        }),
                        ...(Object.hasOwn(action?.payload || {}, 'engagementLetterStatus') && {
                            engagementLetterStatus: action.payload.engagementLetterStatus,
                        }),
                        ...(Object.hasOwn(action?.payload || {}, 'customFields') && {
                            customFields: {
                                ...company.customFields,
                                ...action.payload.customFields,
                            },
                        }),
                        // ...(action.payload.hasOwnProperty('monitoringStatus') && {
                        //     monitoringStatus: action.payload.monitoringStatus,
                        // }),
                        ...createOwnerUpdate(action, company),
                        ...(typeof commentToUpdate === 'object' && commentToUpdate !== null ? { companyComment: commentToUpdate } : {}),
                        ...(shouldSetFollowUpDetails && {
                            followUpDetails: {
                                text: 'Turned Off',
                                subText: 'Last: ' + formatFollowUpDateTime(new Date()),
                            },
                        }),
                    };
                }
                return company;
            }),
        },
    };
};

const prepareCommentUpdate = (commentData) => {
    const { additionalData } = commentData || {};
    if (!additionalData?.isMostRecentCommentChange) return null;
    const clone = structuredClone(
        additionalData?.isDelete && additionalData?.newMostRecentComment ? additionalData.newMostRecentComment : { ...commentData },
    );
    if (!clone.activityDate) clone.activityDate = new Date().toISOString();
    delete clone.additionalData;
    return clone;
};

const setSelectedFetchType = (state, action) => {
    return {
        ...state,
        portfolio: {
            ...state.portfolio,
            fetchedType: action?.payload,
            currentPageNumber: 1,
        },
    };
};

const setPortfolioPageNumber = (state, action) => {
    return {
        ...state,
        portfolio: {
            ...state.portfolio,
            currentPageNumber: action?.payload,
        },
    };
};

const setDealDrawer = (state, action) => {
    return {
        ...state,
        dealDrawer: {
            ...action.payload,
        },
    };
};

const setSingleCompanyDetails = (state, action) => {
    return {
        ...state,
        selectedCompany: action.payload,
    };
};

const fetchInvestorPermissionsSuccess = (state, action) => {
    return {
        ...state,
        permissions: {
            ...state.permissions,
            fetching: false,
            permissionsFetched: true,
            SCAN: action?.payload?.scan || action?.payload?.SCAN || ACCESS_TYPES.LOCKED,
            WATCH: action?.payload?.watch || action?.payload?.WATCH || ACCESS_TYPES.LOCKED,
            TRACK: action?.payload?.track || action?.payload?.TRACK || ACCESS_TYPES.LOCKED,
            DEALS: action?.payload?.deals || action?.payload?.DEALS || ACCESS_TYPES.LOCKED,
            onboardingTabs: action?.payload?.onboardingTabs || [],
            showMagicUpload: action?.payload?.showMagicUpload || false,
            showDataGap: action?.payload?.showDataGap || false,
            // TRACK: 'TRIAL',
        },
    };
    //@TODO remove the upper and lowercase comparisons. just spread action.payload after checking api is sending all permissions in uppercase
};

const showUnlockAicaAndAddToLeadsConfirmation = (state, action: { payload: ShowUnlockAicaAndAddToLeadsPayload }) => {
    return {
        ...state,
        unlockAicaReportAndAddToLeads: {
            ...(action.payload?.showModal
                ? {
                      showConfirmationModal: true,
                      assesseeOrgId: action.payload?.assesseeOrgId || '',
                      orgName: action.payload?.orgName || '',
                      source: action.payload?.source,
                  }
                : {
                      showConfirmationModal: false,
                      assesseeOrgId: '',
                      orgName: '',
                      source: '',
                  }),
        },
    };
};

const setShowInviteUser = (state, action) => {
    return {
        ...state,
        showInviteUser: action.payload,
        inviteUserCallback: undefined,
    };
};

const setShowInviteUserWithCallback = (state, action) => {
    return {
        ...state,
        showInviteUser: action.payload.show,
        inviteUserCallback: action.payload.onSuccess,
    };
};

const setShowGlobalSearch = (state, action) => {
    return {
        ...state,
        showGlobalSearch: action.payload,
    };
};

const setAddToWatchlistModal = (state, action) => {
    const incomingData = action.payload.data || {};
    return {
        ...state,
        addToWatchlistModal: {
            show: action.payload.show,
            data: incomingData,
            onSuccess: action.payload.onSuccess ?? null,
        },
    };
};

const setShowComposeEmail = (state, action) => {
    return {
        ...state,
        composeEmailData: {
            ...state.composeEmailData,
            showComposeEmailModal: action.payload.show,
            selectedRequirements: action.payload.selectedRequirements,
            orgName: action.payload.orgName,
            source: action.payload.source,
            orgId: action.payload.orgId,
            lenderId: action.payload.lenderId,
            dealId: action.payload.dealId,
            partnerOrgId: action.payload.partnerOrgId,
        },
    };
};

const setCreateMandateModal = (state, action) => {
    return {
        ...state,
        createMandateModal: {
            ...state.createMandateModal,
            show: action.payload.show,
        },
    };
};

const setConfigTableModal = (state, action) => {
    return {
        ...state,
        configTableModal: {
            show: action.payload.show ?? false,
            data: action.payload.data || {},
        },
    };
};

const fetchTabLockStatusSuccess = (state, action) => {
    // Merge so a partial map (e.g. tabsList: ['debtDetails'] after Credit Bureau directors load)
    // does not replace the whole lock state and make other tabs look unlocked.
    const previous = state.tabLockStatus && typeof state.tabLockStatus === 'object' ? state.tabLockStatus : {};
    return {
        ...state,
        tabLockStatus: {
            ...previous,
            ...action.payload,
        },
    };
};

const setModulePollingList = (state, action) => {
    return {
        ...state,
        modulePollingList: action.payload,
    };
};

const setTaskTrackerMap = (state, action) => {
    return {
        ...state,
        taskTrackerMap: action.payload,
    };
};

const setDealFilters = (state, action) => {
    return {
        ...state,
        dealFilters: action.payload,
    };
};

const setShowManageTeamDrawer = (state, action) => {
    return {
        ...state,
        showManageTeamDrawer: action.payload,
    };
};

const resetDealFilters = (state, action) => {
    const allStatusesSelected = !!action?.payload?.allStatusesSelected;
    const newDealFilters = {
        ...defaultDealFilters,
        ...(!allStatusesSelected && { statuses: [] }),
    };

    return {
        ...state,
        dealFilters: newDealFilters,
    };
};

const setAicaDebtProductConfig = (state, action) => {
    return {
        ...state,
        aicaDebtProductConfig: {
            ...action.payload,
        },
    };
};

const setCustomFilterDrawer = (state, action) => {
    const { appliedFilters, columnCustomization, ...otherPayload } = action.payload;

    return {
        ...state,
        customFilterDrawer: {
            ...state.customFilterDrawer,
            ...otherPayload,
            appliedFilters:
                appliedFilters !== undefined
                    ? {
                          ...(state.customFilterDrawer.appliedFilters || {}),
                          ...appliedFilters,
                      }
                    : state.customFilterDrawer.appliedFilters,
            columnCustomization:
                columnCustomization !== undefined
                    ? {
                          ...(state.customFilterDrawer.columnCustomization || {}),
                          ...columnCustomization,
                      }
                    : state.customFilterDrawer.columnCustomization,
        },
    };
};

const openColumnCustomizationDrawer = (state, action) => {
    const { sourceKey, selectedColumns } = action.payload;
    const currentColumnCustomization = state.customFilterDrawer.columnCustomization || {
        open: false,
        sourceKey: '',
        selectedColumns: [],
        tempSelectedColumns: [],
        isModified: false,
    };

    return {
        ...state,
        customFilterDrawer: {
            ...state.customFilterDrawer,
            columnCustomization: {
                ...currentColumnCustomization,
                open: true,
                sourceKey: sourceKey || '',
                selectedColumns: selectedColumns || currentColumnCustomization.selectedColumns,
                tempSelectedColumns: selectedColumns || currentColumnCustomization.selectedColumns,
                isModified: false,
            },
        },
    };
};

const closeColumnCustomizationDrawer = (state) => {
    const currentColumnCustomization = state.customFilterDrawer.columnCustomization || {
        open: false,
        sourceKey: '',
        selectedColumns: [],
        tempSelectedColumns: [],
        isModified: false,
    };

    return {
        ...state,
        customFilterDrawer: {
            ...state.customFilterDrawer,
            columnCustomization: {
                ...currentColumnCustomization,
                open: false,
                tempSelectedColumns: currentColumnCustomization.selectedColumns, // Reset temp to current
                isModified: false,
            },
        },
    };
};

const setColumnCustomizationState = (state, action) => {
    const currentColumnCustomization = state.customFilterDrawer.columnCustomization || {
        open: false,
        sourceKey: '',
        selectedColumns: [],
        tempSelectedColumns: [],
        isModified: false,
    };

    return {
        ...state,
        customFilterDrawer: {
            ...state.customFilterDrawer,
            columnCustomization: {
                ...currentColumnCustomization,
                ...action.payload,
            },
        },
    };
};

const updateSelectedColumns = (state, action) => {
    const { selectedColumns } = action.payload;
    const currentColumnCustomization = state.customFilterDrawer.columnCustomization || {
        open: false,
        sourceKey: '',
        selectedColumns: [],
        tempSelectedColumns: [],
        isModified: false,
    };

    // Prevent unnecessary updates if columns haven't changed
    if (JSON.stringify(currentColumnCustomization.selectedColumns) === JSON.stringify(selectedColumns)) {
        return state;
    }

    return {
        ...state,
        customFilterDrawer: {
            ...state.customFilterDrawer,
            columnCustomization: {
                ...currentColumnCustomization,
                selectedColumns: selectedColumns || [],
                tempSelectedColumns: selectedColumns || [], // Keep temp in sync
                isModified: false, // This is initialization, not user modification
            },
        },
    };
};

const updateTempSelectedColumns = (state, action) => {
    const { tempSelectedColumns } = action.payload;
    const currentColumnCustomization = state.customFilterDrawer.columnCustomization || {
        open: false,
        sourceKey: '',
        selectedColumns: [],
        tempSelectedColumns: [],
        isModified: false,
    };

    return {
        ...state,
        customFilterDrawer: {
            ...state.customFilterDrawer,
            columnCustomization: {
                ...currentColumnCustomization,
                tempSelectedColumns: tempSelectedColumns || [],
                isModified: JSON.stringify(tempSelectedColumns) !== JSON.stringify(currentColumnCustomization.selectedColumns),
            },
        },
    };
};

const applyColumnCustomization = (state, action) => {
    const currentColumnCustomization = state.customFilterDrawer.columnCustomization || {
        open: false,
        sourceKey: '',
        selectedColumns: [],
        tempSelectedColumns: [],
        isModified: false,
    };
    const currentTempColumns = currentColumnCustomization.tempSelectedColumns;

    return {
        ...state,
        customFilterDrawer: {
            ...state.customFilterDrawer,
            columnCustomization: {
                ...currentColumnCustomization,
                selectedColumns: currentTempColumns,
                isModified: false,
            },
        },
    };
};

const resetColumnCustomization = (state, action) => {
    const { defaultColumns } = action.payload;
    const currentColumnCustomization = state.customFilterDrawer.columnCustomization || {
        open: false,
        sourceKey: '',
        selectedColumns: [],
        tempSelectedColumns: [],
        isModified: false,
    };

    return {
        ...state,
        customFilterDrawer: {
            ...state.customFilterDrawer,
            columnCustomization: {
                ...currentColumnCustomization,
                selectedColumns: defaultColumns || [],
                tempSelectedColumns: defaultColumns || [],
                isModified: true,
            },
        },
    };
};

const setDealStatusConfig = (state, action) => {
    const allDealViewAndMandateViewOptions = [
        ...(action.payload?.deal || []),
        ...(action.payload?.mandate || []),
        ...(action.payload?.others || []),
    ];

    const allStatusOptionsMap = allDealViewAndMandateViewOptions.reduce(
        (acc, option) => {
            acc[option.value] = option;
            return acc;
        },
        {} as Record<string, (typeof allDealViewAndMandateViewOptions)[number]>,
    );

    return {
        ...state,
        deals: {
            ...state.deals,
            dealStatusConfig: {
                ...action.payload,
                statusDropdownOptionsMap: allStatusOptionsMap,
                isDisableRowLogicEnabled: action.payload?.isDisableRowLogicEnabled || false,
            },
        },
    };
};

const triggerRequirementsRefresh = (state, action) => {
    return {
        ...state,
        shouldRefreshRequirements: !state.shouldRefreshRequirements,
    };
};

const setEditUserModal = (state, action) => {
    return {
        ...state,
        editUserModal: {
            show: action.payload.show,
            userData: action.payload.userData || null,
            refetchUsers: action.payload.refetchUsers || false,
        },
    };
};

const fetchOrgInfoSuccess = (state, action) => {
    return {
        ...state,
        orgInfo: {
            ...action?.payload,
            owner: action?.payload?.owner ?? null,
        },
    };
};

const setDeleteUserModal = (state, action) => {
    return {
        ...state,
        deleteUserModal: {
            show: action.payload.show,
            userData: action.payload.userData || null,
        },
    };
};

const setExtendedPollingModules = (state, action) => {
    return {
        ...state,
        extendedPollingModules: action.payload,
    };
};

const reducer = (state: InvestorReducer = initialState, action): InvestorReducer => {
    switch (action.type) {
        case CONSTANTS.SET_IS_FETCHING_CONFIG:
            return setIsFetchingConfig(state, action);
        case CONSTANTS.SET_IS_FETCHING_PORTFOLIO:
            return setIsFetchingPortfolio(state, action);
        case CONSTANTS.SET_IS_FETCHING_DEALS:
            return setIsFetchingDeals(state, action);
        case CONSTANTS.SET_IS_FETCHING_PERMISSIONS:
            return setIsFetchingPermissions(state, action);
        case CONSTANTS.SET_SHOW_UPGRADE_TO_SCAN:
            return setShowUpgradeToScan(state, action);
        case CONSTANTS.SET_SHOW_REFRESH_REPORT:
            return setShowRefreshReport(state, action);
        case CONSTANTS.SET_SHOW_UNLOCK_AICA_CONFIRMATION:
            return showUnlockAicaConfirmation(state, action);
        case CONSTANTS.SET_SHOW_UNLOCK_AICA_AND_ADD_TO_LEADS:
            return showUnlockAicaAndAddToLeadsConfirmation(state, action);
        case CONSTANTS.SET_SHOW_INVITE_USER:
            return setShowInviteUser(state, action);
        case CONSTANTS.SET_SHOW_INVITE_USER_WITH_CALLBACK:
            return setShowInviteUserWithCallback(state, action);
        case CONSTANTS.SET_SHOW_GLOBAL_SEARCH:
            return setShowGlobalSearch(state, action);
        case CONSTANTS.SET_ADD_TO_WATCHLIST_MODAL:
            return setAddToWatchlistModal(state, action);
        case CONSTANTS.SET_COMPOSE_EMAIL_MODAL:
            return setShowComposeEmail(state, action);
        case CONSTANTS.SET_CREATE_MANDATE_MODAL:
            return setCreateMandateModal(state, action);
        case CONSTANTS.SET_GENERIC_CONFIRMATION_MODAL:
            return setConfigTableModal(state, action);
        case CONSTANTS.UPDATE_DEAL_STATUS:
            return updateDealStatus(state, action);
        case CONSTANTS.UPDATE_COMPANY_STATUS:
            return updateCompanyStatus(state, action);
        case CONSTANTS.UPDATE_INVESTOR_DEAL_STATUS:
            return updateInvestorDealStatus(state, action);
        case CONSTANTS.UPDATE_LENDER_USERS:
            return updateInvestorLenderUsers(state, action);
        case CONSTANTS.FETCH_INVESTOR_CONFIG_SUCCESS:
            return fetchInvestorConfigSuccess(state, action);
        case CONSTANTS.FETCH_PORTFOLIO_DATA_SUCCESS:
            return fetchPortfolioDataSuccess(state, action);
        case CONSTANTS.FETCH_OWNER_DATA_SUCCESS:
            return fetchOwnerDataSuccess(state, action);
        case CONSTANTS.FETCH_DEALS_SUCCESS:
            return fetchDealsDataSuccess(state, action);
        case CONSTANTS.CLEAR_DEALS_DATA:
            return clearDealsData(state);
        case CONSTANTS.FETCH_INVESTOR_PERMISSIONS_SUCCESS:
            return fetchInvestorPermissionsSuccess(state, action);
        case CONSTANTS.SET_PORTFOLIO_SELECTED_FETCH_TYPE:
            return setSelectedFetchType(state, action);
        case CONSTANTS.SET_PORTFOLIO_PAGE_NUMBER:
            return setPortfolioPageNumber(state, action);
        case CONSTANTS.UPDATE_DEAL_COMMENTS:
            return updateDealComments(state, action);
        case CONSTANTS.UPDATE_DEAL_CUSTOM_FIELDS:
            return updateDealCustomFields(state, action);
        case CONSTANTS.UPDATE_DEAL_LAST_STATUS_UPDATE_TIME:
            return updateDealLastStatusUpdateTime(state, action);
        case CONSTANTS.UPDATE_DEAL_OWNER:
            return updateDealOwner(state, action);
        case CONSTANTS.SET_DEAL_DRAWER:
            return setDealDrawer(state, action);
        case CONSTANTS.FETCH_SINGLE_COMPANY_DETAILS_SUCCESS:
            return setSingleCompanyDetails(state, action);
        case CONSTANTS.FETCH_TAB_LOCK_STATUS_SUCCESS:
            return fetchTabLockStatusSuccess(state, action);
        case CONSTANTS.SET_MODULE_POLLING_LIST:
            return setModulePollingList(state, action);
        case CONSTANTS.SET_TASK_TRACKER_MAP:
            return setTaskTrackerMap(state, action);
        case CONSTANTS.UPDATE_UNTAGGED_MU_FILE_NUMBER:
            return {
                ...state,
                untaggedCount: action.payload,
            };
        case CONSTANTS.SET_ONE_PAGER_DATA:
            return setOnePagerData(state, action);
        case CONSTANTS.CLOSE_ONE_PAGER:
            return closeOnePager(state, action);
        case CONSTANTS.SET_ENGAGEMENT_LETTER_DATA:
            return setEngagementLetterData(state, action);
        case CONSTANTS.CLOSE_ENGAGEMENT_LETTER:
            return closeEngagementLetter(state, action);
        case CONSTANTS.SET_BUREAU_KYC_CONSENT_DATA:
            return setBureauKycConsentData(state, action);
        case CONSTANTS.CLOSE_BUREAU_KYC_CONSENT:
            return closeBureauKycConsent(state, action);
        case CONSTANTS.SET_NDA_DATA:
            return setNDAData(state, action);
        case CONSTANTS.CLOSE_NDA:
            return closeNDA(state, action);
        case CONSTANTS.SET_APPLICATION_FORM_DATA:
            return setApplicationFormData(state, action);
        case CONSTANTS.CLOSE_APPLICATION_FORM:
            return closeApplicationForm(state, action);
        case CONSTANTS.UPDATE_NEED_INFO_FILE_NUMBER:
            return {
                ...state,
                pendingDataCount: action.payload,
            };
        case CONSTANTS.OPEN_DEAL_APPLICATION_DRAWER:
            return {
                ...state,
                dealApplicationDrawer: {
                    ...state.dealApplicationDrawer,
                    open: action.payload.open,
                    width: action.payload.width,
                    source: action.payload.source,
                    profileGenerationStatus: action.payload.profileGenerationStatus,
                    dealId: action.payload.dealId,
                    showSuccessModal: action.payload.showSuccessModal,
                    heading: action.payload.heading,
                    subHeading: action.payload.subHeading,
                    dealBeneficiaryId: action.payload.dealBeneficiaryId,
                    step: action.payload.step,
                },
            };
        case CONSTANTS.UPDATE_DEAL_APPLICATION_DATA:
            return {
                ...state,
                dealApplicationDrawer: {
                    ...state.dealApplicationDrawer,
                    dealMasterData: action?.payload?.masterData ?? state.dealApplicationDrawer.dealMasterData,
                    allProducts: action?.payload?.allProducts ?? state.dealApplicationDrawer.allProducts,
                    ...(action?.payload?.step !== undefined && { step: action.payload.step }),
                    ...(action?.payload?.updatedLendersMap !== undefined && { updatedLendersMap: action.payload.updatedLendersMap }),
                    ...(action?.payload?.disabledFields !== undefined && { disabledFields: action.payload.disabledFields }),
                },
            };
        case CONSTANTS.SET_PLATFORM_USER_DATA:
            return {
                ...state,
                platformUserData: action.payload,
            };
        case CONSTANTS.SET_DEAL_FILTERS:
            return setDealFilters(state, action);
        case CONSTANTS.RESET_DEAL_FILTERS:
            return resetDealFilters(state, action);
        case CONSTANTS.SET_SHOW_MANAGE_TEAM_DRAWER:
            return setShowManageTeamDrawer(state, action);
        case CONSTANTS.FETCH_AICA_DEBT_PRODUCT_CONFIG:
            return setAicaDebtProductConfig(state, action);
        case CONSTANTS.INITIALISE_COMPANIES_LIST_FILTERS_CONFIG:
            return initialiseConfigForCompaniesListFilters(state, action);
        case CONSTANTS.SELECT_COMPANIES_LIST_FILTER_VALUES:
            return setCompaniesListFilterValues(state, action);
        case CONSTANTS.RESET_COMPANIES_LIST_FILTER_VALUES:
            return resetCompaniesListFilterValues(state, action);
        case CONSTANTS.SET_CUSTOM_FILTER_DRAWER:
            return setCustomFilterDrawer(state, action);
        case CONSTANTS.OPEN_COLUMN_CUSTOMIZATION_DRAWER:
            return openColumnCustomizationDrawer(state, action);
        case CONSTANTS.CLOSE_COLUMN_CUSTOMIZATION_DRAWER:
            return closeColumnCustomizationDrawer(state);
        case CONSTANTS.SET_COLUMN_CUSTOMIZATION_STATE:
            return setColumnCustomizationState(state, action);
        case CONSTANTS.UPDATE_SELECTED_COLUMNS:
            return updateSelectedColumns(state, action);
        case CONSTANTS.UPDATE_TEMP_SELECTED_COLUMNS:
            return updateTempSelectedColumns(state, action);
        case CONSTANTS.APPLY_COLUMN_CUSTOMIZATION:
            return applyColumnCustomization(state, action);
        case CONSTANTS.RESET_COLUMN_CUSTOMIZATION:
            return resetColumnCustomization(state, action);
        case CONSTANTS.SET_DEAL_STATUS_CONFIG:
            return setDealStatusConfig(state, action);
        case CONSTANTS.TRIGGER_REQUIREMENTS_REFRESH:
            return triggerRequirementsRefresh(state, action);
        case CONSTANTS.FETCH_PARTNER_DATA_SUCCESS:
            return fetchPartnerDataSuccess(state, action);
        case CONSTANTS.SET_EDIT_USER_MODAL:
            return setEditUserModal(state, action);
        case CONSTANTS.FETCH_ORG_INFO_SUCCESS:
            return fetchOrgInfoSuccess(state, action);
        case CONSTANTS.SET_DELETE_USER_MODAL:
            return setDeleteUserModal(state, action);
        case CONSTANTS.SET_DEAL_TERMS_LOGGING_DRAWER_PREFILLED_STATUS:
            return {
                ...state,
                dealTermsLoggingDrawer: {
                    ...state.dealTermsLoggingDrawer,
                    prefilledStatus: action.payload,
                },
            };
        case CONSTANTS.SET_EXTENDED_POLLING_MODULES:
            return setExtendedPollingModules(state, action);
        default:
            return state;
    }
};

export default reducer;
